import { z } from "zod";
import { type Node, type Edge, type ReactFlowInstance } from "@xyflow/react";
import { atom } from "jotai";
import {
  nodesAtom,
  edgesAtom,
  processTourSteps,
  showActiveAnnotationNodeSetter,
  isGeneratingTourAtom,
  isGeneratingExplanationAtom,
  nodeIdToExplainAtom,
  tourIntroStepAtom,
  nodeAtomFamily,
} from "@zapier/canvas-state";
import { calculateAnnotationXOffset } from "./positioningHelpers";

const DESCRIPTION_MIN_CHARS = 100;
const DESCRIPTION_MAX_CHARS = 144;

export const tourStepSchema = z.object({
  header: z.string(),
  description: z.string().min(DESCRIPTION_MIN_CHARS).max(DESCRIPTION_MAX_CHARS),
  parentId: z.string(),
  stepNumber: z.number().int().min(0),
});

type RawTourStep = z.infer<typeof tourStepSchema>;

/**
 * Unified setter for creating annotation nodes
 * Tours and explanations are the same operation - just different parameters
 */
export const renderAnnotationNodesSetter = atom(
  null,
  (
    get,
    set,
    params: {
      rawTourStepsData: RawTourStep[];
      reactFlowInstance: ReactFlowInstance;
    },
  ) => {
    const { rawTourStepsData, reactFlowInstance } = params;

    // Get the atom value once and reuse it
    const nodeIdToExplain = get(nodeIdToExplainAtom);
    const isTour = !nodeIdToExplain;

    if (!rawTourStepsData?.length) {
      if (!nodeIdToExplain) {
        set(isGeneratingTourAtom, false);
        set(isGeneratingExplanationAtom, false);
        set(nodeIdToExplainAtom, null);
      }
      return;
    }

    let annotationData: Array<{
      nodeId: string;
      header: string;
      description: string;
      stepNumber: number;
      totalSteps: number;
    }> = [];

    if (!isTour) {
      // For explanations, use the node ID we already retrieved
      if (!nodeIdToExplain) {
        console.error("No node ID to explain found in atom");
        set(isGeneratingExplanationAtom, false);
        return;
      }

      // Check if the node to explain exists
      const nodeToExplain = get(nodeAtomFamily(nodeIdToExplain));

      if (!nodeToExplain) {
        console.error("Node to explain not found:", nodeIdToExplain);
        set(isGeneratingExplanationAtom, false);
        set(nodeIdToExplainAtom, null);
        return;
      }

      // Process explanation steps (supports multiple nodes in the future)
      annotationData = rawTourStepsData.map((step) => ({
        nodeId: nodeIdToExplain, // Use the nodeId from the atom, not from step data
        header: step.header,
        description: step.description,
        stepNumber: step.stepNumber,
        totalSteps: rawTourStepsData.length,
      }));
    } else {
      // Process tour steps - filter first, then get expensive atoms only if needed
      const filteredSteps = rawTourStepsData.filter((s) => s.stepNumber !== 0);

      if (!filteredSteps.length) {
        set(isGeneratingTourAtom, false);
        return;
      }

      // Only get expensive atoms after we know we have steps to process
      const nodes = get(nodesAtom) as Node[];
      const edges = get(edgesAtom) as Edge[];

      const tourSteps = processTourSteps(filteredSteps, nodes, edges);

      if (!tourSteps.length) {
        set(isGeneratingTourAtom, false);
        return;
      }

      // Set up intro step for tours
      const introStep = rawTourStepsData.find((s) => s.stepNumber === 0) || {
        header: "Welcome to the tour",
        description:
          "This tour will guide you through the key steps in your workflow.",
      };
      const totalSteps = tourSteps.length;
      set(tourIntroStepAtom, {
        header: introStep.header,
        description: introStep.description,
        totalSteps,
      });

      annotationData = tourSteps.map((step) => ({
        nodeId: step.parentId,
        header: step.header,
        description: step.description,
        stepNumber: step.stepNumber,
        totalSteps: totalSteps,
      }));
    }

    // Create annotation nodes (same logic for both types)
    // Get all nodes once and create a lookup map instead of individual atom calls
    const allNodes = get(nodesAtom) as Node[];
    const nodeMap = new Map(allNodes.map((node: Node) => [node.id, node]));

    const annotationNodes = annotationData.map((data) => {
      const parentNode = nodeMap.get(data.nodeId) as Node | undefined;

      return {
        id: `_ANNOTATION_${data.stepNumber}`,
        type: "annotation",
        position: { x: calculateAnnotationXOffset(parentNode), y: 0 },
        parentId: data.nodeId,
        hidden: isTour, // Explanations visible, tours hidden initially
        data: {
          header: data.header,
          description: data.description,
          stepNumber: data.stepNumber,
          totalSteps: data.totalSteps,
          isLastStep: data.stepNumber === data.totalSteps,
        },
      };
    });

    // Update nodes - remove all annotations and add new ones
    set(nodesAtom, (prevNodes: Node[]) => {
      // Use reduce to filter out annotation nodes and optionally deselect all nodes in one pass
      const nonAnnotationNodes = prevNodes.reduce((acc: Node[], node) => {
        // Skip annotation nodes (clean slate)
        if (node.type === "annotation") {
          return acc;
        }

        // For tours, deselect all nodes to remove highlights and toolbars
        // For explanations, keep selection as is
        if (isTour && node.selected) {
          acc.push({ ...node, selected: false });
        } else {
          acc.push(node);
        }

        return acc;
      }, []);

      return [...nonAnnotationNodes, ...annotationNodes];
    });

    // Complete the operation and clear loading states
    if (!isTour) {
      // Explanation: show annotation and clear loading state
      set(showActiveAnnotationNodeSetter, 1);
      set(isGeneratingExplanationAtom, false);
    } else {
      // Tour: clear loading state and setup UI
      set(isGeneratingTourAtom, false);

      // Wait for nodes to be added before panning and zooming
      setTimeout(() => {
        reactFlowInstance.fitView({
          includeHiddenNodes: false,
          padding: 0.1,
          minZoom: 0.3,
          maxZoom: 0.9,
          duration: 200,
        });
      }, 0);

      // Remove tour query parameter if it exists, so on page refresh, tour won't start again
      const url = new URL(window.location.href);
      url.searchParams.delete("tour");
      window.history.replaceState({}, "", url.toString());
    }

    return {
      content: [
        {
          type: "text",
          text: isTour ? "Tour started!" : "Explanation created for the step!",
        },
      ],
    };
  },
);

renderAnnotationNodesSetter.debugLabel = "renderAnnotationNodesSetter";
